import {
  type Control,
  Controller,
  type FieldValues,
  type Path,
} from "react-hook-form";

import { Combobox, Input, Text } from "@alpacahq/ui/old";

import { USA_STATES_MAP } from "./constants";

interface StateOption {
  label: string;
  value: string;
}

interface StateInputProps<T extends FieldValues> {
  isRequired?: boolean;
  isInvalid?: boolean;
  errorMsg?: string;
  name: Path<T>;
  // TContext/TTransformedValues generics stay open: react-hook-form v7 brands them
  // differently depending on whether a resolver is present.
  control: Control<T, any, any>;
  placeholder?: string;
  disabled?: boolean;
}

const stateOptions: StateOption[] = Object.entries(USA_STATES_MAP).map(
  ([abbreviation, fullName]) => ({
    label: fullName,
    value: abbreviation,
  }),
);

const searchPredicate = (searchText: string, option: StateOption) => {
  const text = searchText.toLowerCase();
  return (
    option.label.toLowerCase().includes(text) ||
    option.value.toLowerCase().includes(text)
  );
};

export const StateInput = <T extends FieldValues>({
  isRequired = false,
  isInvalid = false,
  errorMsg,
  name,
  control,
  placeholder = "Select State",
  disabled = false,
}: StateInputProps<T>) => {
  return (
    <Controller<T>
      name={name}
      control={control}
      rules={{ required: isRequired }}
      render={({ field: { onChange, value, name } }) => (
        <div className="flex flex-col gap-2">
          {disabled ? (
            <Input value={placeholder} disabled className="h-9" />
          ) : (
            <Combobox<StateOption>
              className="h-9"
              name={name}
              value={stateOptions.find((s) => s.value === value) ?? null}
              options={stateOptions}
              getOptionLabel={(s) => s.label}
              getOptionKey={(s) => s.value}
              searchPredicate={searchPredicate}
              onChange={(s) => onChange(s?.value ?? "")}
              placeholder={placeholder}
            />
          )}
          {isInvalid && errorMsg && (
            <Text className="truncate text-rose-500 text-xs">{errorMsg}</Text>
          )}
        </div>
      )}
    />
  );
};
